import { ChartFrame } from 'components/ChartFrame'
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useElementSize } from 'usehooks-ts'
import { stack, scaleLinear, scaleTime, stackOffsetNone } from 'd3'
import { global_locales, virus_colors, virusnames } from 'components/constants'
import { useDate } from 'Globals/useDate'

const xTicks = ['2021-01-01', '2022-01-01', '2023-01-01']
const yTicks = [0, 50, 100, 150]
const svgHeight = 400

const locales = global_locales.virusovertime

export const VirusOverTime = ({
  colorMode = 'light',
  lang = 'de',
}: {
  colorMode?: 'dark' | 'light' | undefined
  lang?: 'de' | 'fr'
}) => {
  const [data, setData] = useState<any[] | undefined>()
  const [rawData, setRawData] = useState<any[] | undefined>([])
  const [containerWidth, setContainerWidth] = useState<number>(300)
  const [containerHeight, setContainerHeight] = useState<number>(svgHeight)
  const [containerRef, { width, height }] = useElementSize()
  const svgRef = useRef<SVGSVGElement>(null)
  const endDate = useDate()
  const [toolTipData, setToolTipData] = useState<TooltipData | undefined>(
    undefined
  )
  useEffect(() => {
    setContainerWidth(width || 100)
  }, [width])

  useEffect(() => {
    fetch(
      'https://storage.googleapis.com/ta-interaktiv/corona-dashboard-2023/chart_all_pathogens_dectections.json'
    )
      .then((a) => a.json())
      .then((a) => {
        const series = stack()
          .keys(Object.keys(virus_colors))
          .offset(stackOffsetNone)
        var stacked = series(a)

        setRawData(a)
        setData(stacked)
      })
  }, [])

  const drawArea = useMemo(
    () => ({
      top: 30,
      left: 30,
      right: containerWidth,
      bottom: containerHeight - 30,
    }),
    [containerWidth, containerHeight, data]
  )

  const x = useMemo(() => {
    return scaleTime()
      .domain([new Date('2020-09-29'), new Date(endDate)])
      .range([drawArea.left, drawArea.right])
  }, [drawArea.left, drawArea.right, endDate])

  const y = useMemo(() => {
    return scaleLinear()
      .domain([0, yTicks[yTicks.length - 1]])
      .range([drawArea.bottom, drawArea.top])
  }, [drawArea.bottom, drawArea.top])

  function prepTooltipData(e: any) {
    const chartx = e.clientX ? e.clientX : e.touches[0].clientX

    var date = new Date(
      x.invert(
        chartx -
          (svgRef.current ? svgRef.current?.getBoundingClientRect().x : 0)
      )
    )
      .toISOString()
      .split('T')[0]

    const others = data?.find((f) => f.key === 'other') || []
    const other_day = others.find(
      (a: any) =>
        new Date(a.data.enddate) >= new Date(date) &&
        new Date(a.data.date) <= new Date(date)
    )
    let _y = 0
    if (other_day) {
      _y = y(other_day[1])
    }
    const dataelement = rawData?.find(
      (a) =>
        new Date(a.enddate) >= new Date(date) &&
        new Date(a.date) <= new Date(date)
    )

    if (dataelement) {
      setToolTipData({ x: date, date, day: dataelement, y: _y })
    }
  }
  return (
    <ChartFrame
      lang={lang}
      title={locales[lang].title}
      timestamp={
        <>
          {new Date(endDate).toLocaleDateString(lang + '-CH', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </>
      }
      lead={locales[lang].lead}
      colorKeyEntries={Object.entries(virus_colors).map((key) => {
        return { label: virusnames[key[0]][lang], color: key[1] }
      })}
      source={locales[lang].source}
      colorMode={colorMode}
    >
      <VirusOverTimeRoot ref={containerRef}>aaa
        <SVG
          colorMode={colorMode}
          ref={svgRef}
          width={'100%'}
          height={containerHeight + 'px'}
          onMouseMove={prepTooltipData}
          onTouchMove={prepTooltipData}
          // onTouchStart={prepTooltipData}
          onMouseOut={() => setToolTipData(undefined)}
        >
          <g className='xTicks'>
            {xTicks.map((date) => {
              return (
                <g
                  transform={`translate(${x(new Date(date))},${
                    drawArea.bottom
                  })`}
                >
                  <line y1={10} y2={0} stroke='var(--text-color)' />
                  <text
                    textAnchor='middle'
                    y={26}
                    fill={colorMode === 'dark' ? 'white' : '#202346'}
                  >
                    {new Date(date).toLocaleDateString(lang + '-CH', {
                      month: 'short',
                      year: 'numeric',
                    })}
                  </text>
                </g>
              )
            })}
          </g>
          <g className='yTicks'>
            {yTicks.map((val) => {
              return (
                <g transform={`translate(0,${y(val)})`}>
                  <line x2={drawArea.left - 4} stroke='var(--text-color)' />
                  <line
                    x2={drawArea.left - 4}
                    x1={drawArea.right}
                    stroke='var(--text-color)'
                    opacity={0.1}
                  />
                  <text
                    x={drawArea.left - 4}
                    y={16}
                    textAnchor='end'
                    fill={colorMode === 'dark' ? 'white' : '#202346'}
                  >
                    {val}
                  </text>
                </g>
              )
            })}
          </g>
          <g className='yLabel'>
            {/* <text  y={drawArea.top-10}>Anzahl Nachweise pro Woche</text> */}
          </g>
          {data &&
            data.map((virus: any) => {
              return (
                <g>
                  {virus.map((m: any) => {
                    const bottom = m[0]
                    const top = m[1]
                    const values = m.data
                    const date = values.date
                    const key = virus.key
                    return (
                      <line
                        x1={x(new Date(date))}
                        x2={x(new Date(date))}
                        y1={y(bottom)}
                        y2={y(top)}
                        stroke={virus_colors[key]}
                        strokeWidth={(width || 100) * 0.005}
                      />
                    )
                  })}
                </g>
              )
            })}
          <line
            x1={0}
            x2={containerWidth}
            y1={drawArea.bottom}
            y2={drawArea.bottom}
            stroke='var(--text-color)'
          />
          {toolTipData && (
            <Tooltip
              lang={lang}
              colorMode={colorMode}
              data={toolTipData}
              drawArea={drawArea}
              width={width || 100}
              x={
                x(new Date(toolTipData.day.date)) - ((width || 100) * 0.005) / 2
              }
              y={toolTipData.y}
            />
          )}
        </SVG>
      </VirusOverTimeRoot>
    </ChartFrame>
  )
}

type TooltipData = {
  x: string
  y: number
  date: string
  day: any
}

const Tooltip = ({
  data,
  drawArea,
  width,
  x,
  y,
  colorMode,
  lang,
}: {
  data: TooltipData
  drawArea: {
    top: number
    left: number
    right: number
    bottom: number
  }
  width: number
  x: number
  y: number
  colorMode: 'dark' | 'light' | undefined
  lang: 'de' | 'fr'
}) => {
  const { date, day } = data
  const date_width = 200
  const direction: 'left' | 'right' = x > width * 0.5 ? 'right' : 'left'

  return (
    <g
      className='tooltip'
      transform={`translate(${x + 2},${drawArea.top - 1})`}
    >
      <foreignObject
        x={
          direction === 'left' ? 5 : direction === 'right' ? -date_width - 5 : 5
        }
        y={0}
        width={date_width}
        height={6 * 32}
        style={{
          backgroundColor: 'var(--site-background)',
        }}
      >
        <body>
          <div
            style={{
              textAlign:
                direction === 'left'
                  ? 'left'
                  : direction === 'right'
                  ? 'right'
                  : 'center',
            }}
          >
            <TTDate>
              {day && day.date && (
                <>
                  {new Date(
                    new Date(day.date).setDate(new Date(day.date).getDate() - 3)
                  ).toLocaleDateString(lang + '-CH', {
                    day: '2-digit',
                    month: 'short',
                  }) +
                    ' – ' +
                    new Date(
                      new Date(day.enddate).setDate(
                        new Date(day.enddate).getDate()
                      )
                    ).toLocaleDateString(lang + '-CH', {
                      day: '2-digit',
                      month: 'short',
                    })}
                </>
              )}
            </TTDate>

            {Object.keys(virus_colors).map((virus) => {
              return (
                <div className='element'>
                  <Swatch style={{ backgroundColor: virus_colors[virus] }} />
                  <Virus>{virusnames[virus][lang]}</Virus>
                  <VirusValue>{day ? day[virus] : 0}</VirusValue>
                </div>
              )
            })}
          </div>
        </body>
      </foreignObject>
      <line
        strokeWidth={2}
        y2={y - drawArea.top - 5}
        fill='none'
        stroke={'var(--text-color)'}
      />
    </g>
  )
}

const TTDate = styled.div`
  display: flex;
  align-items: center;
  flex: 0 0 100%;
  font-weight: 300;
  min-height: 40px;
  padding-left: 10px;
`
const Swatch = styled.div`
  display: inline-block;
  width: 5px;
  height: 15px;
  margin-right: 8px;
`
const Virus = styled.div`
  flex: 0 0 calc(100% - 50px);
  text-align: left;
`
const VirusValue = styled.div`
  flex: 0 0 30px;
  text-align: right;
`

const VirusOverTimeRoot = styled.div``
const SVG = styled.svg<{ colorMode: 'dark' | 'light' | undefined }>`
  overflow: visible;
  /* * {
    pointer-events: none;
  } */
  foreignObject {
    font-weight: 300;
    letter-spacing: 0.4px;
    font-size: 13px;
    line-height: 1em;
    color: var(--text-color);
    -webkit-box-shadow: 0px 6px 6px 0px rgba(0, 0, 0, 0.1);
    box-shadow: 0px 6px 6px 0px rgba(0, 0, 0, 0.1);
    background-color: var(--site-background);
    div.element {
      display: flex;

      border-bottom: ${(props) =>
        props.colorMode === 'dark'
          ? '1px solid rgba(255, 255, 255, 0.2)'
          : '1px solid rgba(255, 255, 255, 0.3)'};
      text-align: left;
      padding: 4px;
      padding-left: 8px;
      &:last-child {
        border-bottom: none;
      }
    }
  }
  .xTicks,
  .yTicks {
    font-size: 12px;
  }
  .yLabel {
    font-size: 13px;
  }

  .tooltip {
    font-size: 14px;
  }
`

function addDays(date: string, days: number) {
  var result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}
